/**
 * PermissionsInstructionsPage
 * Provides browser-specific instructions for enabling camera and microphone permissions
 */

import React from 'react'
import { useLocation } from 'react-router-dom'
import { Box, Typography } from '@mui/material'
import {
  VideoCameraIcon,
  MicrophoneIcon,
  LockClosedIcon,
  InformationCircleIcon,
  Cog6ToothIcon,
  ArrowPathIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'
import { PageWrapper } from '../components'
import { PageHeader } from '../components/PageHeader'
import { INTERAX_COLORS } from '../../constants/colors'
import { useTranslation } from '../hooks/useIntractionTranslate'
import { useMediaPermissions } from '../hooks/useMediaPermissions'

/**
 * Detect browser type
 */
const detectBrowser = (): 'chrome' | 'edge' | 'firefox' | 'safari' | 'unknown' => {
  if (typeof window === 'undefined') return 'unknown'

  const userAgent = window.navigator.userAgent.toLowerCase()

  if (userAgent.includes('edg')) return 'edge'
  if (userAgent.includes('chrome') && !userAgent.includes('edg')) return 'chrome'
  if (userAgent.includes('firefox')) return 'firefox'
  if (userAgent.includes('safari') && !userAgent.includes('chrome')) return 'safari'

  return 'unknown'
}

export const PermissionsInstructionsPage: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const { camera, microphone } = useMediaPermissions()
  const browser = detectBrowser()

  // Detect if we're in facilitator or gamer context
  const isFacilitatorContext = location.pathname.startsWith('/facilitator')
  // Facilitator uses 'profile.permissionsInstructions', gamer uses 'permissionsInstructions'
  const translationPrefix = isFacilitatorContext ? 'facilitator.profile' : 'gamer'

  const renderStep = (stepNumber: number, text: string, icon?: React.ReactNode) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '12px',
        borderRadius: '8px',
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        marginBottom: '8px'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '28px',
          height: '28px',
          borderRadius: '50%',
          backgroundColor: INTERAX_COLORS.heritageBlue,
          color: INTERAX_COLORS.white,
          fontWeight: 600,
          fontSize: '14px'
        }}
      >
        {stepNumber}
      </Box>
      {icon && <Box sx={{ display: 'flex', alignItems: 'center', color: INTERAX_COLORS.white, mt: '2px' }}>{icon}</Box>}
      <Typography
        sx={{
          fontSize: '14px',
          color: INTERAX_COLORS.white,
          lineHeight: 1.6,
          flex: 1
        }}
      >
        {text}
      </Typography>
    </Box>
  )

  const renderBrowserInstructions = () => {
    switch (browser) {
      case 'chrome':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: INTERAX_COLORS.white,
                mb: 1
              }}
            >
              {t(`${translationPrefix}.permissionsInstructions.chrome.title`)}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {renderStep(
                1,
                t(`${translationPrefix}.permissionsInstructions.chrome.step1`),
                <InformationCircleIcon style={{ width: 20, height: 20 }} />
              )}
              {renderStep(
                2,
                t(`${translationPrefix}.permissionsInstructions.chrome.step2`),
                <Cog6ToothIcon style={{ width: 20, height: 20 }} />
              )}
              {renderStep(
                3,
                t(`${translationPrefix}.permissionsInstructions.chrome.step3`),
                <Box sx={{ display: 'flex', gap: '4px' }}>
                  <VideoCameraIcon style={{ width: 20, height: 20 }} />
                  <MicrophoneIcon style={{ width: 20, height: 20 }} />
                </Box>
              )}
              {renderStep(
                4,
                t(`${translationPrefix}.permissionsInstructions.chrome.step4`),
                <ArrowPathIcon style={{ width: 20, height: 20 }} />
              )}
            </Box>
          </Box>
        )

      case 'edge':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: INTERAX_COLORS.white,
                mb: 1
              }}
            >
              {t(`${translationPrefix}.permissionsInstructions.edge.title`)}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {renderStep(
                1,
                t(`${translationPrefix}.permissionsInstructions.edge.step1`),
                <InformationCircleIcon style={{ width: 20, height: 20 }} />
              )}
              {renderStep(
                2,
                t(`${translationPrefix}.permissionsInstructions.edge.step2`),
                <Cog6ToothIcon style={{ width: 20, height: 20 }} />
              )}
              {renderStep(
                3,
                t(`${translationPrefix}.permissionsInstructions.edge.step3`),
                <Box sx={{ display: 'flex', gap: '4px' }}>
                  <VideoCameraIcon style={{ width: 20, height: 20 }} />
                  <MicrophoneIcon style={{ width: 20, height: 20 }} />
                </Box>
              )}
              {renderStep(
                4,
                t(`${translationPrefix}.permissionsInstructions.edge.step4`),
                <ArrowPathIcon style={{ width: 20, height: 20 }} />
              )}
            </Box>
          </Box>
        )

      case 'firefox':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: INTERAX_COLORS.white,
                mb: 1
              }}
            >
              {t(`${translationPrefix}.permissionsInstructions.firefox.title`)}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {renderStep(
                1,
                t(`${translationPrefix}.permissionsInstructions.firefox.step1`),
                <ShieldCheckIcon style={{ width: 20, height: 20 }} />
              )}
              {renderStep(
                2,
                t(`${translationPrefix}.permissionsInstructions.firefox.step2`),
                <Cog6ToothIcon style={{ width: 20, height: 20 }} />
              )}
              {renderStep(
                3,
                t(`${translationPrefix}.permissionsInstructions.firefox.step3`),
                <Box sx={{ display: 'flex', gap: '4px' }}>
                  <VideoCameraIcon style={{ width: 20, height: 20 }} />
                  <MicrophoneIcon style={{ width: 20, height: 20 }} />
                </Box>
              )}
              {renderStep(
                4,
                t(`${translationPrefix}.permissionsInstructions.firefox.step4`),
                <ArrowPathIcon style={{ width: 20, height: 20 }} />
              )}
            </Box>
          </Box>
        )

      case 'safari':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: INTERAX_COLORS.white,
                mb: 1
              }}
            >
              {t(`${translationPrefix}.permissionsInstructions.safari.title`)}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {renderStep(
                1,
                t(`${translationPrefix}.permissionsInstructions.safari.step1`),
                <Cog6ToothIcon style={{ width: 20, height: 20 }} />
              )}
              {renderStep(
                2,
                t(`${translationPrefix}.permissionsInstructions.safari.step2`),
                <InformationCircleIcon style={{ width: 20, height: 20 }} />
              )}
              {renderStep(
                3,
                t(`${translationPrefix}.permissionsInstructions.safari.step3`),
                <Box sx={{ display: 'flex', gap: '4px' }}>
                  <VideoCameraIcon style={{ width: 20, height: 20 }} />
                  <MicrophoneIcon style={{ width: 20, height: 20 }} />
                </Box>
              )}
              {renderStep(
                4,
                t(`${translationPrefix}.permissionsInstructions.safari.step4`),
                <LockClosedIcon style={{ width: 20, height: 20 }} />
              )}
            </Box>
          </Box>
        )

      default:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: INTERAX_COLORS.white,
                mb: 1
              }}
            >
              {t(`${translationPrefix}.permissionsInstructions.generic.title`)}
            </Typography>
            <Box sx={{ color: INTERAX_COLORS.white, fontSize: '14px', lineHeight: 1.8 }}>
              <Typography sx={{ mb: 1 }}>
                {t(`${translationPrefix}.permissionsInstructions.generic.description`)}
              </Typography>
            </Box>
          </Box>
        )
    }
  }

  return (
    <PageWrapper>
      <PageHeader
        title={t(`${translationPrefix}.permissionsInstructions.title`)}
        subtitle={t(`${translationPrefix}.permissionsInstructions.subtitle`)}
      />

      {/* Current Permission Status */}
      <Box
        sx={{
          padding: '24px',
          backgroundColor: INTERAX_COLORS.heritageBlue40,
          borderRadius: '12px',
          border: `1px solid ${INTERAX_COLORS.heritageBlue}`,
          marginBottom: '24px'
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            color: INTERAX_COLORS.white,
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}
        >
          <VideoCameraIcon style={{ width: 24, height: 24 }} />
          <MicrophoneIcon style={{ width: 24, height: 24 }} />
          {t(`${translationPrefix}.permissionsInstructions.currentStatus`)}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <VideoCameraIcon style={{ width: 20, height: 20, color: INTERAX_COLORS.white }} />
            <Typography sx={{ fontSize: '14px', color: INTERAX_COLORS.white }}>
              {t(`${translationPrefix}.permissionsInstructions.camera`)}:{' '}
              <Box
                component="span"
                sx={{
                  fontWeight: 600,
                  color:
                    camera === 'granted'
                      ? INTERAX_COLORS.green
                      : camera === 'denied'
                        ? INTERAX_COLORS.redError
                        : INTERAX_COLORS.sun
                }}
              >
                {camera === 'granted'
                  ? t(`${translationPrefix}.permissionsInstructions.enabled`)
                  : camera === 'denied'
                    ? t(`${translationPrefix}.permissionsInstructions.denied`)
                    : t(`${translationPrefix}.permissionsInstructions.prompt`)}
              </Box>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <MicrophoneIcon style={{ width: 20, height: 20, color: INTERAX_COLORS.white }} />
            <Typography sx={{ fontSize: '14px', color: INTERAX_COLORS.white }}>
              {t(`${translationPrefix}.permissionsInstructions.microphone`)}:{' '}
              <Box
                component="span"
                sx={{
                  fontWeight: 600,
                  color:
                    microphone === 'granted'
                      ? INTERAX_COLORS.green
                      : microphone === 'denied'
                        ? INTERAX_COLORS.redError
                        : INTERAX_COLORS.sun
                }}
              >
                {microphone === 'granted'
                  ? t(`${translationPrefix}.permissionsInstructions.enabled`)
                  : microphone === 'denied'
                    ? t(`${translationPrefix}.permissionsInstructions.denied`)
                    : t(`${translationPrefix}.permissionsInstructions.prompt`)}
              </Box>
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Browser-Specific Instructions */}
      <Box
        sx={{
          padding: '24px',
          backgroundColor: INTERAX_COLORS.heritageBlue40,
          borderRadius: '12px',
          border: `1px solid ${INTERAX_COLORS.heritageBlue}`
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            color: INTERAX_COLORS.white,
            marginBottom: '16px'
          }}
        >
          {t(`${translationPrefix}.permissionsInstructions.howToEnable`)}
        </Typography>
        {renderBrowserInstructions()}
      </Box>
    </PageWrapper>
  )
}
